import { MFHInvestmentCase } from '@condo/domain';
import type {
    IBaseMFHInvestmentCase,
    ICalcEngineSpreadsheetResult,
    ID2GVersion,
    IDurableJob,
    IMFHAssetPartnerIcProfile,
    IMFHAssetPartnerInputAssumptionConfig,
    IMFHAssetPartnerPlacementConfig,
    IMFHInvestmentCaseInputs,
    IMFHInvestmentCaseRentIndexCondensedFields,
    IMFHInvestmentCaseVersionChangedField,
    IMFHInvestmentCaseVersionInputSnapshot,
    IMFHInvestmentCaseVersionSummary,
    MFHInvestmentCaseType,
    MFHD2GSettingExitApproach,
    MFHInvestmentCaseReadCopySyncStatus,
} from '@condo/domain';
import type { DeepPartial } from '@condo/helpers';
import type { IBlock } from '@condo/sheets';
import { getClient } from '../api-client';

export const fetchMFHICBlocks = async (icVersion: string): Promise<Record<string, IBlock[]>> => {
    const config = { url: `/calculations/${icVersion}/metadata/blocks.json`, method: 'get' as const, withCredentials: false };
    return getClient('calcengine')
        .request<Record<string, IBlock[]>>(config)
        .then(response => response.data);
};

export const fetchMFHInvestmentCases = async (params: {
    mfhId: number;
    investmentCaseType?: MFHInvestmentCaseType;
    includeDeletedInvestmentCases?: boolean;
}): Promise<MFHInvestmentCase[]> =>
    getClient('basecamp')
        .get(`/mfh/${params.mfhId}/finance/investment-cases`, {
            params: {
                investmentCaseType: params.investmentCaseType,
                includeDeletedInvestmentCases: params.includeDeletedInvestmentCases,
            },
        })
        .then(response => MFHInvestmentCase.fromArray(response.data.investmentCases));

export const restoreMFHInvestmentCase = async (params: { mfhId: number; mfhInvestmentCaseId: number }): Promise<void> =>
    getClient('basecamp').put(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/restore`);

export const getMFHInvestmentCase = async (params: { mfhId: number; mfhInvestmentCaseId: number }): Promise<MFHInvestmentCase> =>
    getClient('basecamp')
        .get(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}`)
        .then(response => new MFHInvestmentCase(response.data.investmentCase));

export const getApprovedMFHInvestmentCase = async (params: { mfhId: number }): Promise<MFHInvestmentCase | null> =>
    getClient('basecamp')
        .get(`/mfh/${params.mfhId}/finance/investment-cases/approved`)
        .then(response => (response.data.investmentCase ? new MFHInvestmentCase(response.data.investmentCase) : null));

export const syncMFHInvestmentCaseReadCopy = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
}): Promise<{ readCopySyncStatus?: MFHInvestmentCaseReadCopySyncStatus }> =>
    getClient('basecamp')
        .post(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/sync-read-copy`)
        .then(response => response.data);

export const downloadMfhInvestmentCaseSheet = async (params: { mfhId: number; mfhInvestmentCaseId: number }): Promise<IDurableJob> =>
    getClient('basecamp')
        .get(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/download-mfh-investment-case-sheet`)
        .then(response => response.data);

export const uploadMfhInvestmentCaseReviewedSheet = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    tmpBucketPath: string;
    filename: string;
}): Promise<{ documentId: number; path: string }> => {
    return getClient('basecamp')
        .post(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/upload-reviewed-sheet`, {
            tmpBucketPath: params.tmpBucketPath,
            filename: params.filename,
        })
        .then(response => response.data);
};

export const getSyncedMFHInvestmentCaseInputs = async (params: { mfhId: number; mfhInvestmentCaseId: number }): Promise<IMFHInvestmentCaseInputs> =>
    getClient('basecamp')
        .get(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/synced-mfh-inputs`)
        .then(response => response.data.caseInputs);

export const readMFHInvestmentCaseOutputs = async (params: { mfhId: number; mfhInvestmentCaseId: number }): Promise<IBaseMFHInvestmentCase> =>
    getClient('basecamp')
        .get(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/read-outputs`)
        .then(response => response.data);

export const setMFHInvestmentCaseAsPrimary = async (params: { mfhId: number; mfhInvestmentCaseId: number; isPrimaryCase: boolean }): Promise<void> =>
    getClient('basecamp').post(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/primary`, {
        isPrimaryCase: params.isPrimaryCase,
    });

export const setMFHInvestmentCaseAsProfilePrimary = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    isProfilePrimaryCase: boolean;
}): Promise<void> =>
    getClient('basecamp').post(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/profile-primary`, {
        isProfilePrimaryCase: params.isProfilePrimaryCase,
    });

export const syncMFHInvestmentCase = async (params: { mfhId: number; mfhInvestmentCaseId: number; changeableFields: string[] }): Promise<MFHInvestmentCase> =>
    getClient('basecamp')
        .post(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/sync`, { changeableFields: params.changeableFields })
        .then(response => new MFHInvestmentCase(response.data.investmentCase));

export const recalculateMFHInvestmentCase = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    caseInputs?: DeepPartial<IMFHInvestmentCaseInputs>;
    saveOnly?: boolean;
    forceRecalculation?: boolean;
    forceMappingRetrieval?: boolean;
    forcePredictions?: boolean;
}): Promise<MFHInvestmentCase> =>
    getClient('basecamp')
        .post(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/recalculate`, {
            forceMappingRetrieval: params.forceMappingRetrieval,
            saveOnly: params.saveOnly,
            forceRecalculation: params.forceRecalculation,
            forcePredictions: params.forcePredictions,
            caseInputs: params.caseInputs,
        })
        .then(response => new MFHInvestmentCase(response.data.investmentCase));

export const deleteMFHInvestmentCase = async (params: { mfhId: number; mfhInvestmentCaseId: number; hardDelete?: boolean }): Promise<void> =>
    getClient('basecamp').delete(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}`, {
        params: { hardDelete: params.hardDelete },
    });

export const deleteAllMFHInvestmentCases = async (params: { mfhId: number }): Promise<IDurableJob> =>
    getClient('basecamp')
        .delete<IDurableJob>(`/mfh/${params.mfhId}/finance/investment-cases`)
        .then(response => response.data);

export const fetchD2GVersions = async (): Promise<ID2GVersion[]> =>
    getClient('basecamp')
        .get(`/mfh/finance/d2g-versions`)
        .then(response => response.data.sortedVersions);

export const getMFHInvestmentCaseCalcengineInputs = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
}): Promise<{
    inputs: Record<string, any>;
}> =>
    getClient('basecamp')
        .post<{ inputs: Record<string, any> }>(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/calc-engine-inputs`)
        .then(r => r.data);

export const getCalculatedInvestmentCaseValues = async (params: { mfhId: number; mfhInvestmentCaseId: number }): Promise<ICalcEngineSpreadsheetResult> =>
    getClient('basecamp')
        .get<{ calculatedValues: ICalcEngineSpreadsheetResult }>(
            `/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/calculated-values`,
        )
        .then(r => r.data.calculatedValues);

export interface MFHInvestmentCaseVersionsResponse {
    supported: boolean;
    versions: IMFHInvestmentCaseVersionSummary[];
}

export interface MFHInvestmentCaseVersionInputPreviewResponse {
    targetSnapshot: IMFHInvestmentCaseVersionInputSnapshot;
    changedFromPreviousInputs: IMFHInvestmentCaseVersionChangedField[];
    changedFromCurrentInputs: IMFHInvestmentCaseVersionChangedField[];
}

export interface MFHInvestmentCaseVersionResultPreviewResponse {
    targetD2gVersion: string;
    targetOutput: Partial<IBaseMFHInvestmentCase>;
    changedFromPreviousOutputs: IMFHInvestmentCaseVersionChangedField[];
    changedFromCurrentOutputs: IMFHInvestmentCaseVersionChangedField[];
    calculationInputHash: string;
    hasCalculationInputDrift: boolean;
}

export interface MFHInvestmentCaseVersionSheetPreviewResponse {
    targetD2gVersion: string;
    calculatedValues: ICalcEngineSpreadsheetResult;
    changedFromPreviousOutputs: IMFHInvestmentCaseVersionChangedField[];
    changedFromCurrentOutputs: IMFHInvestmentCaseVersionChangedField[];
    calculationInputHash: string;
    hasCalculationInputDrift: boolean;
}

export const fetchMFHInvestmentCaseVersions = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    limit?: number;
    beforeVersionNumber?: number;
}): Promise<MFHInvestmentCaseVersionsResponse> =>
    getClient('basecamp')
        .get<MFHInvestmentCaseVersionsResponse>(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/versions`, {
            params: {
                limit: params.limit,
                beforeVersionNumber: params.beforeVersionNumber,
            },
        })
        .then(r => r.data);

export const getMFHInvestmentCaseVersionInputPreview = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    mfhInvestmentCaseVersionId: number;
}): Promise<MFHInvestmentCaseVersionInputPreviewResponse> =>
    getClient('basecamp')
        .get<MFHInvestmentCaseVersionInputPreviewResponse>(
            `/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/versions/${params.mfhInvestmentCaseVersionId}/input-preview`,
        )
        .then(r => r.data);

export const getMFHInvestmentCaseVersionResultPreview = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    mfhInvestmentCaseVersionId: number;
}): Promise<MFHInvestmentCaseVersionResultPreviewResponse> =>
    getClient('basecamp')
        .get<MFHInvestmentCaseVersionResultPreviewResponse>(
            `/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/versions/${params.mfhInvestmentCaseVersionId}/result-preview`,
        )
        .then(r => r.data);

export const getMFHInvestmentCaseVersionSheetPreview = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    mfhInvestmentCaseVersionId: number;
}): Promise<MFHInvestmentCaseVersionSheetPreviewResponse> =>
    getClient('basecamp')
        .get<MFHInvestmentCaseVersionSheetPreviewResponse>(
            `/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/versions/${params.mfhInvestmentCaseVersionId}/sheet-preview`,
        )
        .then(r => r.data);

export const revertMFHInvestmentCaseVersion = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    mfhInvestmentCaseVersionId: number;
}): Promise<{ investmentCase: MFHInvestmentCase; version?: IMFHInvestmentCaseVersionSummary }> =>
    getClient('basecamp')
        .post<{
            investmentCase: IBaseMFHInvestmentCase;
            version?: IMFHInvestmentCaseVersionSummary;
        }>(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/versions/${params.mfhInvestmentCaseVersionId}/revert`)
        .then(r => ({
            investmentCase: new MFHInvestmentCase(r.data.investmentCase),
            version: r.data.version,
        }));

export const getMFHRentIndexLookup = async (params: { mfhId: number }): Promise<IMFHInvestmentCaseRentIndexCondensedFields[]> =>
    getClient('basecamp')
        .get<{ rentIndex: IMFHInvestmentCaseRentIndexCondensedFields[] }>(`/mfh/${params.mfhId}/finance/investment-cases/rent-index-lookup`)
        .then(r => r.data.rentIndex ?? []);

export const fetchAssetPartnerIcProfiles = async (params?: { companyId?: number }): Promise<IMFHAssetPartnerIcProfile[]> =>
    getClient('basecamp')
        .get(`/mfh/finance/asset-partner-ic-profiles`, { params: { companyId: params?.companyId } })
        .then(response => response.data.profiles ?? []);

export const createAssetPartnerIcProfile = async (params: {
    companyId: number;
    name: string;
    config: IMFHAssetPartnerInputAssumptionConfig;
    placementConfig?: IMFHAssetPartnerPlacementConfig;
    isDefault?: boolean;
}): Promise<IMFHAssetPartnerIcProfile> =>
    getClient('basecamp')
        .post(`/mfh/finance/asset-partner-ic-profiles`, params)
        .then(response => response.data.profile);

export const updateAssetPartnerIcProfile = async (params: {
    assetPartnerIcProfileId: number;
    name?: string;
    config?: IMFHAssetPartnerInputAssumptionConfig;
    placementConfig?: IMFHAssetPartnerPlacementConfig;
    isDefault?: boolean;
}): Promise<IMFHAssetPartnerIcProfile> =>
    getClient('basecamp')
        .patch(`/mfh/finance/asset-partner-ic-profiles/${params.assetPartnerIcProfileId}`, {
            name: params.name,
            config: params.config,
            placementConfig: params.placementConfig,
            isDefault: params.isDefault,
        })
        .then(response => response.data.profile);

export const deleteAssetPartnerIcProfile = async (params: { assetPartnerIcProfileId: number }): Promise<void> => {
    await getClient('basecamp').delete(`/mfh/finance/asset-partner-ic-profiles/${params.assetPartnerIcProfileId}`);
};

export const createEmptyMFHInvestmentCase = async (data: {
    mfhId: number;
    mfhPredictionId?: number;
    d2gVersion: string;
    name?: string;
    assetPartnerProfileId: number;
    exitApproach?: MFHD2GSettingExitApproach;
}): Promise<MFHInvestmentCase> =>
    getClient('basecamp')
        .post(`/mfh/${data.mfhId}/finance/investment-cases`, data)
        .then(response => new MFHInvestmentCase(response.data.investmentCase));

export const prepareMFHInvestmentCase = async (data: { mfhId: number; mfhInvestmentCaseId: number; mfhPredictionId?: number }): Promise<IDurableJob> =>
    getClient('basecamp')
        .post(`/mfh/${data.mfhId}/finance/investment-cases/${data.mfhInvestmentCaseId}/prepare`, { mfhPredictionId: data.mfhPredictionId })
        .then(response => response.data);

export const changeAndRecreateMFHInvestmentCase = async (data: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    d2gVersion?: string;
    configId?: string;
}): Promise<MFHInvestmentCase> =>
    getClient('basecamp')
        .post(`/mfh/${data.mfhId}/finance/investment-cases/${data.mfhInvestmentCaseId}/change-and-recreate`, {
            configId: data.configId,
            d2gVersion: data.d2gVersion,
        })
        .then(response => new MFHInvestmentCase(response.data.investmentCase));

export const copyMFHInvestmentCase = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    d2gVersion?: string;
    configId?: string;
    assetPartnerProfileId?: number;
    exitApproach?: MFHD2GSettingExitApproach;
    name?: string;
    copyCaseInputsToNewVersion?: boolean;
    exactCopy?: boolean;
}): Promise<IDurableJob> =>
    getClient('basecamp')
        .post<IDurableJob>(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/copy`, {
            d2gVersion: params.d2gVersion,
            configId: params.configId,
            assetPartnerProfileId: params.assetPartnerProfileId,
            exitApproach: params.exitApproach,
            name: params.name,
            copyCaseInputsToNewVersion: params.copyCaseInputsToNewVersion,
            exactCopy: params.exactCopy,
        })
        .then(response => response.data);

export const copyAndChangeAssetPartnerMFHInvestmentCase = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    assetPartnerProfileId: number;
    name?: string;
    preserveValues?: boolean;
    updateAssetPartnerAssumptions?: boolean;
}): Promise<IDurableJob> =>
    getClient('basecamp')
        .post<IDurableJob>(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/copy-and-change-asset-partner`, {
            assetPartnerProfileId: params.assetPartnerProfileId,
            name: params.name,
            preserveValues: params.preserveValues,
            updateAssetPartnerAssumptions: params.updateAssetPartnerAssumptions,
        })
        .then(response => response.data);

export const copyMFHInvestmentCaseToAssetManagement = async (
    params: {
        mfhId: number;
        mfhInvestmentCaseId: number;
        name?: string;
        validFrom?: string;
    },
    requestOpts?: { skipErrorHandler?: boolean },
): Promise<IDurableJob> =>
    getClient('basecamp')
        .post<IDurableJob>(
            `/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}/copy-to-asset-management`,
            {
                name: params.name,
                validFrom: params.validFrom,
            },
            { skipErrorHandler: requestOpts?.skipErrorHandler } as any,
        )
        .then(response => response.data);

export const updateMFHInvestmentCase = async (params: {
    mfhId: number;
    mfhInvestmentCaseId: number;
    updatableInvestmentCase: Partial<IBaseMFHInvestmentCase>;
}): Promise<IBaseMFHInvestmentCase> =>
    getClient('basecamp')
        .patch(`/mfh/${params.mfhId}/finance/investment-cases/${params.mfhInvestmentCaseId}`, { mfhInvestmentCase: params.updatableInvestmentCase })
        .then(response => response.data.investmentCase);

export const getMFHInvestmentCaseAggregate = async (params: { mfhId: number }): Promise<{ investmentCaseAggregate: any } | null> => {
    try {
        const response = await getClient('basecamp').get(`/mfh/${params.mfhId}/finance/investment-case-aggregate`);
        return response.data.investmentCaseAggregate ? response.data : null;
    } catch (error) {
        return null;
    }
};

export const generateMFHInvestmentCaseAggregate = async (params: { mfhId: number; mfhInvestmentCaseIds: number[] }): Promise<any> =>
    getClient('basecamp')
        .post(`/mfh/${params.mfhId}/finance/investment-case-aggregate/generate`, {
            mfhInvestmentCaseIds: params.mfhInvestmentCaseIds,
        })
        .then(response => response.data);
